import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK_ITEM } from '../../../../../../../../webcomponents.constants';
import {
  networkValidators,
  typeValidators,
  identificationValidators,
  macValidators,
  ipValidatorByIdentification,
} from '../../utils/validators';
import {
  DEFAULT_MAC_ADDRESS,
  getFirewallNetworkSearchableKeywords,
  getNetworkIdentification,
  getNetworkTypeFormOptions,
} from '../../../../../utils/uiSettings/firewall/firewall.values';
import { GzDropdownWebComponent } from '../../../../../../../../ui/gzDropdown';
import { FirewallNetworkIdentification } from '@nirvana/shared/dtos/types/policies/common';
import { toggleDisabled } from '../../../../../../../../utils/htmlElement.utils';
import { RequiredValidator } from '../../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../../ui/gzForm/validators/customizable.validator';
import { FirewallNetworkSearchableKeywords } from '../../../../../utils/uiSettings/firewall/firewall.types';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK_ITEM)
export class GzFirewallNetworkItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  protected searchableKeywords = getFirewallNetworkSearchableKeywords();

  @query('#itemNetwork')
  itemNetwork: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#itemType')
  itemType: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#itemIdentification')
  itemIdentification: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @query('#itemMac')
  itemMac: GzFormFieldWebComponent<GzInputWebComponent, string>;

  @query('#itemIp')
  itemIp: GzFormFieldWebComponent<GzInputWebComponent, string>;

  /**
   * Some fields from the current component are connected. When an external error is set,
   * any change on another field should remove external validation from server.
   */
  private shouldBeRevalidatedOnFieldChange = false;

  public setItemNetworkError(error: string) {
    this.itemNetwork.setError(error);
  }

  public setItemIdentificationError(error: string) {
    this.shouldBeRevalidatedOnFieldChange = true;
    this.itemIdentification.setError(error);
    this.itemMac.setError(error);
    this.itemIp.setError(error);
  }

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field input--w25">
        <gz-form-field id="itemNetwork">
          <gz-input
            gz-form-input
            label="${translateFn('Name')}"
            placeholder="${translateFn('Enter the network name')}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemType">
          <gz-single-select
            gz-form-input
            label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.NetworkProfile].translatedLabel}"
            searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.NetworkProfile].label}"
            filterOptions="${JSON.stringify(getNetworkTypeFormOptions())}"
          ></gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemIdentification">
          <gz-single-select
            @selectionChange="${this.handleIdentificationFieldChange}"
            gz-form-input
            label="${translateFn('Identification')}"
            filterOptions="${JSON.stringify(getNetworkIdentification())}"
          ></gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field input--w25">
        <gz-form-field id="itemMac">
          <gz-input
            gz-form-input
            label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Mac].translatedLabel}"
            searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Mac].label}"
            placeholder="${translateFn('Enter the MAC address')}"
            @change="${e => {
              if (this.shouldBeRevalidatedOnFieldChange) {
                this.shouldBeRevalidatedOnFieldChange = false;
                this.itemIdentification.validate(true);
                this.itemIp.validate(true);
              }
            }}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field input--w25">
        <gz-form-field id="itemIp">
          <gz-input
            gz-form-input
            label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Ip].translatedLabel}"
            searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Ip].label}"
            placeholder="${translateFn('Enter the IP address')}"
            @change="${e => {
              if (this.shouldBeRevalidatedOnFieldChange) {
                this.shouldBeRevalidatedOnFieldChange = false;
                this.itemIdentification.validate(true);
                this.itemMac.validate(true);
              }
            }}"
          ></gz-input>
        </gz-form-field>
      </div>
    `;
  }

  private handleIdentificationFieldChange(event: CustomEvent): void {
    const isNetwork = event.detail.selectedValue === FirewallNetworkIdentification.NETWORK;

    this.itemMac.value = isNetwork ? DEFAULT_MAC_ADDRESS : '';
    this.itemMac.resetField();
    toggleDisabled(this.itemMac, isNetwork);

    this.itemIp.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => ipValidatorByIdentification(value, event.detail.selectedValue)),
    ];
  }

  focusFirstInput(): void {
    this.itemNetwork.Input.inputFocus();
  }

  createFormGroup(): void {
    this.itemNetwork.Validators = networkValidators();
    this.itemType.Validators = typeValidators();
    this.itemIdentification.Validators = identificationValidators();
    this.itemMac.Validators = macValidators();
    this.itemIp.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => ipValidatorByIdentification(value)),
    ];

    this.formGroup = new FormGroup({
      network: this.itemNetwork,
      type: this.itemType,
      identification: this.itemIdentification,
      domainMac: this.itemMac,
      ip: this.itemIp,
    });
  }

  resetForm(): void {
    this.itemNetwork.value = '';
    this.itemNetwork.resetField();

    this.itemType.value = '';
    this.itemType.resetField();
    this.itemType.validate();

    this.itemIdentification.value = '';
    this.itemIdentification.resetField();
    this.itemIdentification.validate();

    this.itemMac.value = '';
    this.itemMac.resetField();
    toggleDisabled(this.itemMac, false);

    this.itemIp.value = '';
    this.itemIp.resetField();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm();
  }
}
